import React, { useEffect, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  Button,
  Badge,
  Form,
  Input,
  Modal,
  Space,
  Table,
  message,
  Select,
  Tabs,
  Checkbox,
  Switch,
  Tooltip,
  Progress,
} from "antd";

// Firebase
import firebase from "firebase/compat/app";

import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  CloseOutlined,
  LockOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";

import { stamp } from "../../../modules/helpers/audit";

import moment from "moment";
import _ from "lodash";

import getHighlightedText from "../../../modules/helpers/utils";

const { confirm } = Modal;
const { TabPane } = Tabs;

// Motif de connexion déléguée : même minimum que la fonction generateImpersonationToken
const DELEGATION_REASON_MIN = 10;

function sorterFn(property) {
  return (a, b) => `${a[property]}`.toLowerCase().localeCompare(`${b[property]}`.toLowerCase());
}

// Password validation helper
const validatePasswordStrength = (password) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };

  const passedChecks = Object.values(checks).filter(Boolean).length;
  const strength = (passedChecks / 4) * 100;

  return { checks, strength };
};

function PasswordForm({ form }) {
  const [password, setPassword] = useState("");
  const [passwordValidation, setPasswordValidation] = useState({
    checks: {
      length: false,
      uppercase: false,
      lowercase: false,
      special: false,
    },
    strength: 0,
  });

  // Suppress ResizeObserver error (benign error from Progress component)
  useEffect(() => {
    const resizeObserverErrHandler = (e) => {
      if (e.message === "ResizeObserver loop completed with undelivered notifications.") {
        const resizeObserverErr = document.getElementById("webpack-dev-server-client-overlay");
        if (resizeObserverErr) {
          resizeObserverErr.style.display = "none";
        }
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", resizeObserverErrHandler);

    return () => {
      window.removeEventListener("error", resizeObserverErrHandler);
    };
  }, []);

  const validatePassword = (rule, value, callback) => {
    if (value && value !== password) {
      callback("Les mots de passe sont différents");
    } else {
      callback();
    }
  };

  const validatePasswordRequirements = (rule, value) => {
    if (!value) {
      return Promise.reject("Veuillez entrer un nouveau mot de passe");
    }

    const validation = validatePasswordStrength(value);
    setPasswordValidation(validation);

    const errors = [];
    if (!validation.checks.length) {
      errors.push("au moins 8 caractères");
    }
    if (!validation.checks.uppercase) {
      errors.push("au moins 1 lettre majuscule");
    }
    if (!validation.checks.lowercase) {
      errors.push("au moins 1 lettre minuscule");
    }
    if (!validation.checks.special) {
      errors.push("au moins 1 caractère spécial");
    }

    if (errors.length > 0) {
      return Promise.reject(`Le mot de passe doit contenir ${errors.join(", ")}`);
    }

    return Promise.resolve();
  };

  const getStrengthColor = (strength) => {
    if (strength <= 25) return "#ff4d4f";
    if (strength <= 50) return "#faad14";
    if (strength <= 75) return "#1890ff";
    return "#52c41a";
  };

  const getStrengthText = (strength) => {
    if (strength <= 25) return "Faible";
    if (strength <= 50) return "Moyen";
    if (strength <= 75) return "Bon";
    return "Fort";
  };

  return (
    <Form layout="vertical" form={form}>
      <Form.Item
        name="new_password"
        label="Nouveau mot de passe"
        rules={[{ validator: validatePasswordRequirements }]}
      >
        <Input
          value={password}
          onChange={(e) => {
            const val = e.target.value;
            setPassword(val);
            setPasswordValidation(validatePasswordStrength(val));
          }}
          prefix={<LockOutlined className="site-form-item-icon" />}
          type="password"
          name="new_password"
          autoComplete="new-password"
        />
      </Form.Item>

      {password && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ marginRight: 8 }}>Force du mot de passe:</span>
            <strong style={{ color: getStrengthColor(passwordValidation.strength) }}>
              {getStrengthText(passwordValidation.strength)}
            </strong>
          </div>
          <Progress
            percent={passwordValidation.strength}
            showInfo={false}
            strokeColor={getStrengthColor(passwordValidation.strength)}
            size="small"
          />

          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, marginBottom: 8 }}>
              <strong>Exigences du mot de passe:</strong>
            </div>
            <Space direction="vertical" size={4}>
              <div style={{ fontSize: 12 }}>
                {passwordValidation.checks.length ? (
                  <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                ) : (
                  <CloseCircleOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
                )}
                Au moins 8 caractères
              </div>
              <div style={{ fontSize: 12 }}>
                {passwordValidation.checks.uppercase ? (
                  <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                ) : (
                  <CloseCircleOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
                )}
                Au moins 1 lettre majuscule
              </div>
              <div style={{ fontSize: 12 }}>
                {passwordValidation.checks.lowercase ? (
                  <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                ) : (
                  <CloseCircleOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
                )}
                Au moins 1 lettre minuscule
              </div>
              <div style={{ fontSize: 12 }}>
                {passwordValidation.checks.special ? (
                  <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                ) : (
                  <CloseCircleOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
                )}
                Au moins 1 caractère spécial (!@#$%^&* etc.)
              </div>
            </Space>
          </div>
        </div>
      )}

      <Form.Item
        name="confirm_new_password"
        label="Confirmer votre nouveau mot de passe"
        rules={[
          { required: true, message: "Veuillez confirmer votre nouveau mot de passe" },
          { validator: validatePassword },
        ]}
      >
        <Input
          prefix={<LockOutlined className="site-form-item-icon" />}
          type="password"
          name="confirm_new_password"
          autoComplete="new-password"
        />
      </Form.Item>
    </Form>
  );
}

function UserForm({ values, form, companies, canEditRoles }) {
  const canEdit2FA = useMemo(() => {
    return values && values.has2FAActivated;
  }, [values]);

  return values ? (
    <Form layout="vertical" form={form} initialValues={values}>
      <Form.Item
        name="company"
        label="Compte / Entreprise"
        rules={[{ required: true, message: "Veuillez selectionner un compte" }]}
      >
        <Select
          showSearch
          placeholder="Sélectionnez un compte"
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {companies.map((company) => (
            <Select.Option value={company.id} key={company.key}>
              {company.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="last_name"
        label="Nom"
        rules={[{ required: true, message: "Veuillez entrer un nom" }]}
      >
        <Input placeholder="Ex: Dupont" />
      </Form.Item>

      <Form.Item
        name="first_name"
        label="Prénom"
        rules={[{ required: true, message: "Veuillez entrer un prénom" }]}
      >
        <Input placeholder="Ex: Jean" />
      </Form.Item>

      <Form.Item name="address_line_1" label="Adresse ligne 1" rules={[{ required: false }]}>
        <Input placeholder="Adresse postale, boîte postale" />
      </Form.Item>

      <Form.Item name="address_line_2" label="Adresse ligne 2" rules={[{ required: false }]}>
        <Input placeholder="Appartement, suite, unité, immeuble, étage, etc." />
      </Form.Item>

      <Form.Item name="postal_code" label="Code postal" rules={[{ required: false }]}>
        <Input placeholder="Ex: 75008" />
      </Form.Item>

      <Form.Item name="city" label="Ville" rules={[{ required: false }]}>
        <Input placeholder="Ex: Paris" />
      </Form.Item>

      <Form.Item name="email" label="Adresse email">
        <Input placeholder="Ex: jean.dupont@gmail.com" disabled />
      </Form.Item>

      <Form.Item name="phone" label="Téléphone">
        <Input placeholder="Ex: 0123456789" />
      </Form.Item>

      {/* Rôles : modifiables par un admin Solidoc uniquement (décision 05/10/2026) */}
      <Form.Item
        name="is_company_admin"
        valuePropName="checked"
        tooltip={canEditRoles ? "" : "Rôles modifiables par un administrateur Solidoc"}
      >
        <Checkbox disabled={!canEditRoles}>Administrateur de compte</Checkbox>
      </Form.Item>

      <Form.Item name="is_account_admin" valuePropName="checked">
        <Checkbox disabled={!canEditRoles}>Administrateur d'équipe</Checkbox>
      </Form.Item>

      <Form.Item name="isOperator" valuePropName="checked">
        <Checkbox disabled={!canEditRoles}>Opérateur</Checkbox>
      </Form.Item>

      <Form.Item name="isAdmin" valuePropName="checked">
        <Checkbox disabled={!canEditRoles}>Super administrateur</Checkbox>
      </Form.Item>
      <Form.Item
        name="has2FAActivated"
        valuePropName="checked"
        label="Solidoc Authenticator"
        tooltip={canEdit2FA ? "" : "Seul l'utilisateur peut activer Solidoc Authenticator"}
      >
        <Switch disabled={!canEdit2FA} checkedChildren={"Activé"} unCheckedChildren={"Désactivé"} />
      </Form.Item>
    </Form>
  ) : (
    <Form layout="vertical" form={form} initialValues={values}>
      <Form.Item
        name="last_name"
        label="Nom"
        rules={[{ required: true, message: "Veuillez entrer un nom" }]}
      >
        <Input placeholder="Ex: Dupont" />
      </Form.Item>

      <Form.Item
        name="first_name"
        label="Prénom"
        rules={[{ required: true, message: "Veuillez entrer un prénom" }]}
      >
        <Input placeholder="Ex: Jean" />
      </Form.Item>

      <Form.Item
        name="email"
        label="Adresse email"
        rules={[
          { required: true, message: "Veuillez entrer un email" },
          { type: "email", message: "Veuillez entrer un email valide" },
        ]}
      >
        <Input placeholder="Ex: jean.dupont@gmail.com" />
      </Form.Item>
    </Form>
  );
}

function UserModal({
  visible,
  user,
  onSubmit,
  onCancel,
  isFormLoading,
  setIsFormLoading,
  companies,
  canEditRoles,
}) {
  const [form] = Form.useForm();
  const [selectedTab, setSelectedTab] = useState("informations");

  useEffect(() => {
    form.resetFields();
  }, [form, user]);

  useEffect(() => {
    if (visible === true) {
      setIsFormLoading(false);
    }
  }, [visible]);

  return (
    <Modal
      visible={visible}
      title={user ? "Modifier un utilisateur" : "Inviter un nouvel utilisateur"}
      okText={user ? "Modifier" : "Inviter"}
      canceltext="Annuler"
      maskClosable={false}
      onCancel={() => {
        onCancel();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            setIsFormLoading(true);
            onSubmit(selectedTab, values, form.resetFields);
          })
          .catch(() => {
            // Validation failed
          });
      }}
      okButtonProps={{ loading: isFormLoading }}
      bodyStyle={{ paddingTop: 0 }}
    >
      <Tabs
        activeKey={selectedTab}
        onChange={(value) => {
          setSelectedTab(value);
        }}
      >
        <TabPane tab="Informations" key="informations" />
        <TabPane tab="Mot de passe" key="password" />
      </Tabs>
      {selectedTab === "informations" ? (
        <UserForm form={form} values={user} companies={companies} canEditRoles={canEditRoles} />
      ) : (
        <PasswordForm form={form} />
      )}
    </Modal>
  );
}

export default function UsersTable({
  company,
  companies,
  setLastUpdate,
  searchTerms,
  allUsersView,
}) {
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState(null);
  const currentUser = useSelector((state) => state.user, shallowEqual);
  const isSolidocAdmin = !!(currentUser && currentUser.isAdmin);
  // Connexion déléguée : utilisateur visé + motif saisi
  const [delegationTarget, setDelegationTarget] = useState(null);
  const [delegationReason, setDelegationReason] = useState("");
  const [isDelegating, setIsDelegating] = useState(false);

  const db = firebase.firestore();
  const resendInvite = firebase.app().functions("europe-west1").httpsCallable("resendInvite");
  const deleteUser = firebase.app().functions("europe-west1").httpsCallable("deleteUser");
  const disableUser = firebase.app().functions("europe-west1").httpsCallable("disableUser");
  const enableUser = firebase.app().functions("europe-west1").httpsCallable("enableUser");
  const updateUserPassword = firebase
    .app()
    .functions("europe-west1")
    .httpsCallable("updateUserPassword");
  const generateImpersonationToken = firebase
    .app()
    .functions("europe-west1")
    .httpsCallable("generateImpersonationToken");

  // Connexion déléguée (support) : motif obligatoire, 60 minutes au plus, toutes les actions
  // de la session sont tracées au nom de l'admin (décisions du 05/10/2026)
  function handleImpersonate(record) {
    setDelegationReason("");
    setDelegationTarget(record);
  }

  async function startDelegation() {
    const reason = delegationReason.trim();
    if (!delegationTarget || reason.length < DELEGATION_REASON_MIN) return;
    setIsDelegating(true);
    try {
      const result = await generateImpersonationToken({ uid: delegationTarget.uid, reason });
      await firebase.auth().signInWithCustomToken(result.data.token);
      // Reload pour partir d'un état Redux propre sous l'identité cible
      window.location.href = "/";
    } catch (e) {
      console.error(e);
      message.error(e.message || "Erreur lors de l'ouverture de la connexion déléguée");
      setIsDelegating(false);
    }
  }

  useEffect(() => {
    setUsers(company.users);
  }, [company]);

  const columns = [
    {
      title: "Nom",
      dataIndex: "last_name",
      sorter: sorterFn("last_name"),
      defaultSortOrder: "ascend",
      render: (text) => getHighlightedText(text, searchTerms),
    },
    {
      title: "Prénom",
      dataIndex: "first_name",
      sorter: sorterFn("first_name"),
      render: (text) => getHighlightedText(text, searchTerms),
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: sorterFn("email"),
      render: (text) => getHighlightedText(text, searchTerms),
    },
    {
      title: "Téléphone",
      dataIndex: "phone",
    },
    {
      title: "Statut",
      key: "status",
      filters: [
        {
          text: "En attente",
          value: "awaiting",
        },
        {
          text: "Actif",
          value: "active",
        },
        {
          text: "Désactivé",
          value: "disabled",
        },
      ],
      sorter: (a, b) => a.last_invite.diff(b.last_invite),
      onFilter: (value, record) =>
        (value === "disabled" && record.disabled) ||
        (value === "awaiting" && !record.isActive) ||
        (value === "active" && record.isActive && !record.disabled),
      render: (text, record) => (
        <Space size="small">
          {record.isActive ? (
            record.disabled ? (
              <Badge status="error" text="Désactivé" />
            ) : (
              <Badge status="success" text="Actif" />
            )
          ) : (
            record.last_invite && (
              <Badge
                status="warning"
                text={`En attente depuis ${record.last_invite.fromNow(true)}`}
              />
            )
          )}
        </Space>
      ),
    },
    {
      title: "Dernière connexion",
      key: "last_connected",
      sorter: (a, b) => a.last_connected.diff(b.last_connected),
      render: (text, record) => (
        <Space size="small">
          {record.isActive && record.has_connected ? (
            <span>{_.capitalize(record.last_connected.fromNow())}</span>
          ) : (
            <span />
          )}
        </Space>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Space size="small">
          <Button
            onClick={() => {
              setSelectedUser(record);
              setModalVisible(true);
            }}
            type="primary"
            title="Modifier"
            size="small"
            shape="circle"
            icon={<EditOutlined />}
          />
          {isSolidocAdmin && record.isActive && !record.disabled && !record.isAdmin && !record.isOperator && (
            <Tooltip title="Connexion déléguée : agir en tant que cet utilisateur (support, tracé)">
              <Button
                type="default"
                size="small"
                shape="circle"
                icon={<UserSwitchOutlined />}
                onClick={() => handleImpersonate(record)}
              />
            </Tooltip>
          )}
          {record.isActive && !record.disabled && (
            <Button
              type="danger"
              title="Désactiver"
              size="small"
              shape="circle"
              icon={<CloseOutlined />}
              onClick={() => {
                confirm({
                  title: "Désactiver un utilisateur",
                  icon: <ExclamationCircleOutlined />,
                  content: (
                    <div>
                      Êtes-vous sûr de vouloir désactiver l'utilisateur{" "}
                      <strong>
                        {record.first_name} {record.last_name} ({record.email})
                      </strong>
                      ?
                    </div>
                  ),
                  okText: "Désactiver",
                  okType: "danger",
                  onOk: () => {
                    return disableUser({ uid: record.uid })
                      .then(() => {
                        message.success("Utilisateur désactivé");
                        const clonedUsers = users;
                        clonedUsers.splice(_.findIndex(users, { uid: record.uid }), 1, {
                          ...record,
                          disabled: true,
                        });

                        setUsers([...clonedUsers]);
                      })
                      .catch((e) => {
                        console.error(e);
                        message.error("Une erreur est survenue. Veuillez réessayer.");
                      });
                  },
                });
              }}
            />
          )}
          {record.disabled && (
            <Button
              type="primary"
              title="Réactiver"
              size="small"
              shape="circle"
              icon={<CheckCircleOutlined />}
              onClick={() => {
                confirm({
                  title: "Réactiver un utilisateur",
                  icon: <ExclamationCircleOutlined />,
                  content: (
                    <div>
                      Êtes-vous sûr de vouloir réactiver l'utilisateur{" "}
                      <strong>
                        {record.first_name} {record.last_name} ({record.email})
                      </strong>
                      ?
                    </div>
                  ),
                  okText: "Réactiver",
                  onOk: () => {
                    return enableUser({ uid: record.uid })
                      .then(() => {
                        message.success("Utilisateur réactivé");
                        const clonedUsers = users;
                        clonedUsers.splice(_.findIndex(users, { uid: record.uid }), 1, {
                          ...record,
                          disabled: false,
                        });

                        setUsers([...clonedUsers]);
                      })
                      .catch((e) => {
                        console.error(e);
                        message.error("Une erreur est survenue. Veuillez réessayer.");
                      });
                  },
                });
              }}
            />
          )}
          {!record.isActive && (
            <>
              <Button
                type="default"
                title=""
                size="small"
                shape="circle"
                icon={<ReloadOutlined />}
                onClick={() => {
                  confirm({
                    title: "Renvoyer une invitation",
                    content: (
                      <div>
                        Êtes-vous sûr de vouloir renvoyer une invitation à{" "}
                        <strong>
                          {record.first_name} {record.last_name} ({record.email})
                        </strong>{" "}
                        ?
                        <br />
                        <br />
                        Un nouveau mot de passe sera généré et envoyé par email.
                      </div>
                    ),
                    okText: "Renvoyer un invitation",
                    onOk: () => {
                      return resendInvite({ uid: record.uid })
                        .then(() => {
                          message.success("Invitation envoyée");
                        })
                        .catch((e) => {
                          console.error(e);
                          message.error("Une erreur est survenue. Veuillez réessayer.");
                        });
                    },
                  });
                }}
              />
              <Button
                type="danger"
                title="Supprimer"
                size="small"
                shape="circle"
                icon={<DeleteOutlined />}
                onClick={() => {
                  confirm({
                    title: "Supprimer un utilisateur",
                    icon: <ExclamationCircleOutlined />,
                    content: (
                      <div>
                        Êtes-vous sûr de vouloir supprimer l'utilisateur{" "}
                        <strong>
                          {record.first_name} {record.last_name} ({record.email})
                        </strong>
                        ?
                      </div>
                    ),
                    okText: "Supprimer",
                    okType: "danger",
                    onOk: () => {
                      return deleteUser({ uid: record.uid })
                        .then(() => {
                          message.success("Utilisateur supprimé");
                          setUsers([..._.reject(users, record)]);
                        })
                        .catch((e) => {
                          console.error(e);
                          message.error("Une erreur est survenue. Veuillez réessayer.");
                        });
                    },
                  });
                }}
              />
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        title={
          !allUsersView &&
          (() => (
            <h4>
              Utilisateurs du compte: <b>{company.name}</b>
            </h4>
          ))
        }
        bordered
        style={{ marginTop: 16 }}
        columns={columns}
        dataSource={users}
        size="small"
        scroll={{ x: true }}
        pagination={allUsersView && { defaultPageSize: 50 }}
        locale={{ emptyText: "Aucun utilisateur" }}
        loading={allUsersView && users && !users.length}
      />

      <Modal
        visible={!!delegationTarget}
        title="Connexion déléguée"
        okText="Se connecter"
        cancelText="Annuler"
        width={560}
        maskClosable={false}
        confirmLoading={isDelegating}
        okButtonProps={{ disabled: delegationReason.trim().length < DELEGATION_REASON_MIN }}
        onOk={startDelegation}
        onCancel={() => setDelegationTarget(null)}
      >
        {delegationTarget && (
          <>
            <p>
              Vous allez agir en tant que{" "}
              <strong>
                {delegationTarget.first_name} {delegationTarget.last_name} ({delegationTarget.email})
              </strong>{" "}
              pendant 60 minutes au plus. Chaque action sera tracée à votre nom dans le journal.
            </p>
            <p>
              ⚠️ Cela <strong>remplacera votre session admin</strong> dans cette fenêtre : ouvrez
              l'app en navigation privée pour la conserver.
            </p>
            <Form layout="vertical">
              <Form.Item
                label="Motif (obligatoire)"
                help={`${DELEGATION_REASON_MIN} caractères minimum — ex. : ticket 1234, envoi bloqué à l'étape 3`}
              >
                <Input.TextArea
                  rows={3}
                  maxLength={500}
                  value={delegationReason}
                  onChange={(e) => setDelegationReason(e.target.value)}
                  autoFocus
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* Modal */}
      <UserModal
        companies={companies}
        canEditRoles={isSolidocAdmin}
        visible={modalVisible}
        user={selectedUser}
        onCancel={() => {
          setModalVisible(false);
        }}
        isFormLoading={isFormLoading}
        setIsFormLoading={setIsFormLoading}
        onSubmit={(type, values, resetFields) => {
          if (selectedUser) {
            const shouldRefresh = selectedUser.company.id !== values.company;

            if (type === "informations") {
              db.collection("users")
                .doc(selectedUser.uid)
                .set(
                  {
                    ...values,
                    company: _.find(companies, { id: values.company }).ref,
                    phone2Fa: values.has2FAActivated ? values.phone2Fa : "",
                    last_change: stamp(),
                  },
                  { merge: true }
                )
                .then(() => {
                  if (shouldRefresh) {
                    setLastUpdate(new Date().getTime());
                  } else {
                    const clonedUsers = users;
                    clonedUsers.splice(_.findIndex(users, { uid: selectedUser.uid }), 1, {
                      ...selectedUser,
                      ...values,
                    });

                    setUsers([...clonedUsers]);
                  }

                  resetFields();
                  setModalVisible(false);
                  message.success("Modifications enregistrées");
                })
                .catch((e) => {
                  setIsFormLoading(false);
                  message.error("Une erreur est survenue. Veuillez réessayer.");
                  console.error(e);
                });
            } else {
              updateUserPassword({ userUid: selectedUser.uid, password: values.new_password })
                .then(() => {
                  resetFields();
                  setModalVisible(false);
                  message.success("Mot de passe modifié");
                })
                .catch((e) => {
                  resetFields();
                  setIsFormLoading(false);
                  message.error("Une erreur est survenue. Veuillez réessayer.");
                  console.error(e);
                });
            }
          }
        }}
      />
    </>
  );
}
